import type {TranslationFunction} from 'src/types/language-support';
import type {
  VoicebotState,
  VoicebotAPI,
  DynamicValidations,
  APIVoicebotPromptVariablesData,
} from 'src/types/voicebot-v2';
import {
  parseNodeSchedule,
  normalizeNodeSchedule,
} from 'src/utils/node-schedule';


export const ConfigPageType = Object.freeze({
  SELECT_VOICEBOT: 'SELECT_VOICEBOT',
  VOICEBOT_CONFIG: 'VOICEBOT_CONFIG',
});

/**
 * Converts comma separated prompt variables to object
 * @param {*} vars eg: 'ae_job/title,ae_job/state,ae_candidate/full_name,ae_candidate/first_name,ae_job/city'
 * @returns object { 'ae_job/title': null, 'ae_job/state': null, ...}
 */
export const transformPromptVariables = (
  vars: string | null,
): VoicebotState['prompt_variables'] => {
  const result: Record<string, any> = {};
  if (vars === null || vars === '') {
    return result;
  }

  // Split the input string by commas to get an array of keys
  const keys = vars.split(',');

  keys.forEach((key) => {
    result[key] = null;
  });

  return result;
};

// Changes field names of API response
export const transformStaticVariables = (
  vars: Array<{
    key: string;
    attribute: string;
    display_name: string;
  }>,
): APIVoicebotPromptVariablesData['static_variables'] => {
  return vars.map((variable) => ({
    name: variable.key,
    value: variable.display_name,
  }));
};

export const parseVoicebot = (data: VoicebotAPI): VoicebotState => {
  const voicebotParams = data.task.params;
  const {retry_1, retry_2, retry_3} = data;

  if (!voicebotParams) {
    throw new Error('Voiceflow params not found');
  }

  const {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
    communication_method,
    secondary_communication_method,
    whatsapp_template_id,
    whatsapp_template_system_variables,
    whatsapp_template_user_mappings,
    email_sender_name,
    email_sender_address,
    email_receiver_address,
    email_subject,
    email_cc,
    email_bcc,
    email_template_system_variables,
    email_template_user_mappings,
  } = voicebotParams;
  const nodeSchedule = parseNodeSchedule(data);

  return {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
    retry_1,
    retry_2,
    retry_3,
    communication_method,
    secondary_communication_method,
    whatsapp_template_id,
    whatsapp_template_system_variables,
    whatsapp_template_user_mappings,
    email_sender_name,
    email_sender_address,
    email_receiver_address,
    email_subject,
    email_cc,
    email_bcc,
    email_template_system_variables,
    email_template_user_mappings,
    ...nodeSchedule,
  };
};

export const normalizeVoicebot = (
  voicebotState: VoicebotState,
): VoicebotAPI => {
  const {
    retell_bot_id,
    retell_agent_id,
    retell_bot_name,
    retell_bot_description,
    retell_llm_id,
    consent_type,
    max_concurrency_limit,
    service_provider,
    entity_type,
    from_number,
    to_number,
    recipient_name,
    prompt_variables,
    retry_1,
    retry_2,
    retry_3,
    communication_method,
    secondary_communication_method,
    whatsapp_template_id,
    whatsapp_template_system_variables,
    whatsapp_template_user_mappings,
    email_sender_name,
    email_sender_address,
    email_receiver_address,
    email_subject,
    email_cc,
    email_bcc,
    email_template_system_variables,
    email_template_user_mappings,
  } = voicebotState;
  const nodeSchedule = normalizeNodeSchedule(voicebotState);

  return {
    task: {
      params: {
        retell_bot_id,
        retell_agent_id,
        retell_bot_name,
        retell_bot_description,
        retell_llm_id,
        consent_type,
        max_concurrency_limit,
        service_provider,
        entity_type,
        from_number,
        to_number,
        recipient_name,
        prompt_variables,
        communication_method,
        secondary_communication_method,
        whatsapp_template_id,
        whatsapp_template_system_variables,
        whatsapp_template_user_mappings,
        email_sender_name,
        email_sender_address,
        email_receiver_address,
        email_subject,
        email_cc,
        email_bcc,
        email_template_system_variables,
        email_template_user_mappings,
      },
    },
    retry_1,
    retry_2,
    retry_3,
    ...nodeSchedule,
  };
};

export const getErrors = (
  validations: DynamicValidations,
  retryErrors: {
    invalidInput: boolean;
    exceedValidDuration: boolean;
  },
  maxRetry: number,
  t: TranslationFunction,
): {errors: Array<string>; tabsError: Record<string, boolean>} => {
  const errors: Array<string> = [];
  const tabsError: Record<string, boolean> = {
    voiceflow: false,
    retries: false,
    settings: false,
  };
  const {
    callToNumberEmpty,
    callToNumberInvalid,
    recipientNameEmpty,
    ...restValidations
  } = validations || {};

  const whatsappVars = Object.keys(restValidations).filter((key) =>
    key.startsWith('whatsapp_'),
  );

  const emailVars = Object.keys(restValidations).filter((key) =>
    key.startsWith('email_'),
  );

  const voiceflowVars = Object.keys(restValidations).filter((key) =>
    key.startsWith('voiceflow_'),
  );

  const anyVoiceflowEmpty = voiceflowVars
    .filter((key) => key.endsWith('_empty'))
    .some((key) => Boolean(restValidations[key]));

  const anyVoiceflowInvalid = voiceflowVars
    .filter((key) => key.endsWith('_invalid'))
    .some((key) => Boolean(restValidations[key]));

  const anyWhatsappEmpty = whatsappVars
    .filter((key) => key.endsWith('_empty'))
    .some((key) => Boolean(restValidations[key]));

  const anyEmailEmpty = emailVars
    .filter((key) => key.endsWith('_empty'))
    .some((key) => Boolean(restValidations[key]));

  const anySettingsEmpty =
    Boolean(callToNumberEmpty) ||
    Boolean(recipientNameEmpty) ||
    anyWhatsappEmpty ||
    anyEmailEmpty;

  if (anyVoiceflowEmpty || anySettingsEmpty) {
    if (anyVoiceflowEmpty && anySettingsEmpty) {
      errors.push(
        t(
          'Please_fill_the_required_fields_in_Voiceflow_and_Settings',
          'Please fill the required fields in Voiceflow and Settings.',
        ),
      );
      tabsError['voiceflow'] = true;
      tabsError['settings'] = true;
    } else if (anyVoiceflowEmpty) {
      errors.push(
        t(
          'Please_fill_the_required_fields_in_Voiceflow',
          'Please fill the required fields in Voiceflow.',
        ),
      );
      tabsError['voiceflow'] = true;
    } else {
      errors.push(
        t(
          'Please_fill_the_required_fields_in_Settings',
          'Please fill the required fields in Settings.',
        ),
      );
      tabsError['settings'] = true;
    }
  }
  if (anyVoiceflowInvalid) {
    errors.push(
      t(
        'Please_enter_valid_variables_in_Voiceflow',
        'Please enter valid variables in Voiceflow.',
      ),
    );
    tabsError['voiceflow'] = true;
  }
  if (callToNumberInvalid) {
    errors.push(
      `${t(
        'Please_choose_a_valid_call_to_number_from_the_variable_picker_in_Settings',
        'Please choose a valid call to number from the variable picker in Settings',
      )} — ${t('manual_entry_is_not_allowed', 'manual entry is not allowed')}.`,
    );
    tabsError['settings'] = true;
  }
  if (retryErrors.invalidInput || retryErrors.exceedValidDuration) {
    tabsError['retries'] = true;
    if (retryErrors.invalidInput) {
      errors.push(
        `${t(
          'Retries_have_invalid_inputs',
          'Retries have invalid inputs',
        )}. ${t(
          'Please_enter_valid_retry_durations',
          'Please enter valid retry durations',
        )}.`,
      );
    }
    if (retryErrors.exceedValidDuration) {
      errors.push(
        `${t(
          'Total_duration_cannot_be_more_than',
          'Total duration cannot be more than',
        )} ${maxRetry} ${
          maxRetry > 1 ? t('days', 'days') : t('day', 'day')
        } ${t('for_retries', 'for retries')}.`,
      );
    }
  }
  return {errors, tabsError};
};

/**
 * reason will be of this type 'dial_no_answer'
 * returns 'dial no answer'
 */
export const formatFailureReason = (reason: unknown): string => {
  if (!reason || typeof reason !== 'string') {
    return '';
  }
  return reason.split('_').join(' ');
};

/**
 * Populates target object with values from source object where keys match
 * @param {Object} to - Target object to be populated
 * @param {Object} from - Source object containing values to populate from
 * @returns {Object} Updated target object
 */
export const populatePromptVariables = (
  to: {
    [key: string]: string | null | undefined;
  },
  from: {
    [key: string]: string | null | undefined;
  },
): {
  [key: string]: string | null | undefined;
} => {
  const result = {...to} as const;
  Object.keys(result).forEach((key) => {
    if (key in from) {
      // @ts-expect-error - TS2542 - Index signature in type '{ readonly [key: string]: string | null | undefined; }' only permits reading.
      result[key] = from[key];
    }
  });

  return result;
};

export const getUnitAndValue = (delay: VoicebotState['retry_1']['delay']) => {
  const numberOfDays = delay?.date_offset?.days;
  const unit = numberOfDays != null ? 'days' : 'hours';
  const value = numberOfDays ? numberOfDays : delay?.time_offset?.hours ?? 0;
  return {unit, value};
};
